<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div slot="header">
            <i class="fa fa-microchip"></i> Device Configuration
            <div class="card-header-actions">
              <b-button @click="showCreateModal = true" variant="success" size="sm" class="mr-2">
                <i class="fa fa-plus"></i> Add Device
              </b-button>
              <b-button @click="refreshDevices" variant="outline-primary" size="sm" :disabled="loading">
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> Refresh
              </b-button>
            </div>
          </div>

          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Loading devices...</p>
          </div>

          <div v-else-if="error" class="alert alert-danger">
            <strong>Error:</strong> {{ error.message }}
            <b-button @click="refreshDevices" variant="outline-danger" size="sm" class="ml-2">
              Try Again
            </b-button>
          </div>

          <div v-else>
            <div v-if="!devices || devices.length === 0" class="text-center py-4 text-muted">
              <i class="fa fa-microchip fa-3x mb-3"></i>
              <h5>No Devices Found</h5>
              <p>No devices are available for configuration.</p>
            </div>

            <div v-else>
              <div class="mb-3">
                <b-row>
                  <b-col md="8">
                    <b-input-group>
                      <b-form-input
                        v-model="searchTerm"
                        placeholder="Search devices by name, serial, or type..."
                        debounce="300"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-button variant="outline-secondary" @click="searchTerm = ''">
                          <i class="fa fa-times"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-col>
                  <b-col md="4" class="text-right">
                    <b-form-checkbox
                      v-model="showDisabledDevices"
                      switch
                      size="lg"
                    >
                      <span class="ml-2">Show disabled devices</span>
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </div>

              <b-table
                :items="filteredDevices"
                :fields="fields"
                striped
                hover
                responsive
                :per-page="perPage"
                :current-page="currentPage"
                show-empty
                empty-text="No devices match your search criteria"
              >
                <template #cell(status)="data">
                  <b-badge :variant="data.item.enabled ? 'success' : 'secondary'">
                    {{ data.item.enabled ? 'Active' : 'Inactive' }}
                  </b-badge>
                </template>

                <template #cell(signal)="data">
                  <div
                    class="signal-bars"
                    :title="signalData[data.item.id] != null ? 'Signal: ' + signalData[data.item.id] : 'No signal data'"
                  >
                    <template v-if="signalData[data.item.id] != null">
                      <span
                        v-for="bar in 4"
                        :key="bar"
                        class="signal-bar"
                        :class="signalBarClass(data.item.id, bar)"
                      ></span>
                    </template>
                    <span v-else class="text-muted" style="font-size:0.7rem;">—</span>
                  </div>
                </template>

                <template #cell(location)="data">
                  <span v-if="data.item.latitude && data.item.longitude">
                    {{ data.item.latitude.toFixed(4) }}, {{ data.item.longitude.toFixed(4) }}
                  </span>
                  <span v-else class="text-muted">Not set</span>
                </template>

                <template #cell(site)="data">
                  {{ (data.item.site && data.item.site.name) || 'N/A' }}
                </template>

                <template #cell(actions)="data">
                  <!-- The menu must escape .table-responsive's overflow (which clipped it when the
                       table was short) AND be placed correctly when the page is scrolled. With
                       boundary="viewport" alone BootstrapVue sets position-static on the wrapper
                       and Popper positions against the card; that misplaced the menu above the
                       viewport for top rows until the page was scrolled. positionFixed lays the
                       menu out in viewport coordinates, so it ignores ancestor overflow and page
                       scroll alike, and boundary="viewport" then clamps it to the visible area. -->
                  <b-dropdown
                    size="sm"
                    variant="outline-secondary"
                    right
                    no-caret
                    boundary="viewport"
                    :popper-opts="{ positionFixed: true }"
                    menu-class="device-actions-menu"
                  >
                    <template #button-content>
                      <i class="fa fa-ellipsis-v"></i>
                    </template>
                    <b-dropdown-item @click="editDevice(data.item)">
                      <i class="fa fa-cog mr-1"></i> Configure
                    </b-dropdown-item>
                    <b-dropdown-item @click="openActions(data.item)">
                      <i class="fa fa-bolt mr-1"></i> Live Events
                    </b-dropdown-item>
                    <b-dropdown-item @click="openReplace(data.item)">
                      <i class="fa fa-exchange-alt mr-1"></i> Replace
                    </b-dropdown-item>
                    <b-dropdown-item @click="openCalibrate(data.item)">
                      <i class="fa fa-balance-scale mr-1"></i> Calibrate
                    </b-dropdown-item>
                    <b-dropdown-item @click="openLinks(data.item)">
                      <i class="fa fa-link mr-1"></i> Linked Devices
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item @click="runShortcut(data.item, 'getReadings', 'Get Readings', true)">
                      <i class="fa fa-tachometer-alt mr-1"></i> Get Readings
                    </b-dropdown-item>
                    <b-dropdown-item v-if="data.item.type === 'well'" @click="runShortcut(data.item, 'doDepthRead', 'Get Depth Reading', true)">
                      <i class="fa fa-tint mr-1"></i> Get Depth Reading
                    </b-dropdown-item>
                    <b-dropdown-item @click="runShortcut(data.item, 'getBatteryCharge', 'Get Battery Charge', true)">
                      <i class="fa fa-battery-three-quarters mr-1"></i> Get Battery Charge
                    </b-dropdown-item>
                    <b-dropdown-item @click="runShortcut(data.item, 'refreshConfig', 'Refresh Config', false)">
                      <i class="fa fa-sync-alt mr-1"></i> Refresh Config
                    </b-dropdown-item>
                    <b-dropdown-item @click="runShortcut(data.item, 'resetDevice', 'Reset Device', false)">
                      <i class="fa fa-power-off mr-1"></i> Reset Device
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>

              <b-pagination
                v-if="filteredDevices.length > perPage"
                v-model="currentPage"
                :total-rows="filteredDevices.length"
                :per-page="perPage"
                align="center"
                class="mt-3"
              ></b-pagination>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Device Configuration Modal -->
    <DeviceConfigModal
      v-if="selectedDevice && showConfigModal"
      :device="selectedDevice"
      :visible="showConfigModal"
      @close="closeConfigModal"
      @updated="onDeviceConfigUpdated"
    />

    <!-- Device Actions Modal -->
    <DeviceActionsModal
      v-if="selectedDevice && showActionsModal"
      :device="selectedDevice"
      :visible="showActionsModal"
      @close="showActionsModal = false"
    />

    <!-- Create Device Modal -->
    <b-modal
      v-model="showCreateModal"
      title="Add New Device"
      size="lg"
      @ok="createDevice"
      @cancel="resetCreateForm"
      @hidden="resetCreateForm"
      :ok-disabled="!isCreateFormValid"
      ok-title="Add Device"
    >
      <form @submit.prevent="createDevice">
        <b-row>
          <b-col md="6">
            <b-form-group label="Device Name *" label-for="create-name">
              <b-form-input id="create-name" v-model="createForm.name" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Device Type *" label-for="create-type">
              <b-form-select id="create-type" v-model="createForm.type" :options="deviceTypeOptions" required />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Particle Cloud / Identification -->
        <b-card class="mb-3" body-class="py-2 px-3" border-variant="info">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold">
              <i class="fa fa-cloud mr-1 text-info"></i> Particle Cloud Provisioning
            </span>
            <b-form-checkbox v-model="createForm.manualEntry" size="sm" switch>
              <small>Skip — enter serial manually</small>
            </b-form-checkbox>
          </div>

          <template v-if="!createForm.manualEntry">
            <b-form-group label-for="create-particleId" class="mb-1">
              <template #label>
                Particle Device ID or Hardware Serial *
              </template>
              <b-form-input
                id="create-particleId"
                v-model="createForm.particleDeviceId"
                placeholder="e.g. P028AD114KD4YSP (hardware serial) or e00fce68cd7d03658f2740d4 (Particle ID)"
                required
              />
              <small class="form-text text-muted">
                Enter the printed hardware serial from the new device, or the 24-character Particle Device ID if you already have it.
                The Particle device ID will be obtained from Particle Cloud and stored as the device's Serial.
              </small>
            </b-form-group>
          </template>

          <template v-else>
            <b-row>
              <b-col md="6">
                <b-form-group label="Serial Number *" label-for="create-serial" class="mb-1">
                  <b-form-input id="create-serial" v-model="createForm.serial" required />
                  <small class="form-text text-muted">
                    Particle Device ID (e.g. e00fce68cd7d03658f2740d4). Skip the Particle provisioning above only if this device is already in Particle Cloud.
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
          </template>
        </b-card>

        <b-row>
          <b-col md="6">
            <b-form-group label="Leaf Serial *" label-for="create-leafSerial">
              <b-form-input id="create-leafSerial" v-model="createForm.leafSerial" required />
              <small class="form-text text-muted">Internal leaf.ai serial for tracking.</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Version *" label-for="create-version">
              <b-form-select id="create-version" v-model="createForm.version" :options="versionOptions" required />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Subtype" label-for="create-subtype">
              <b-form-input id="create-subtype" v-model="createForm.subtype" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Site" label-for="create-site">
              <b-form-select id="create-site" v-model="createForm.siteId" :options="siteOptions" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4">
            <b-form-group label="Elevation (ft)" label-for="create-elevation">
              <b-form-input id="create-elevation" v-model.number="createForm.elevation" type="number" step="0.01" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Latitude" label-for="create-latitude">
              <b-form-input id="create-latitude" v-model.number="createForm.latitude" type="number" step="0.000001" min="-90" max="90" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Longitude" label-for="create-longitude">
              <b-form-input id="create-longitude" v-model.number="createForm.longitude" type="number" step="0.000001" min="-180" max="180" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Start Date" label-for="create-startDate">
              <b-form-input id="create-startDate" v-model="createForm.startDate" type="datetime-local" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="End Date" label-for="create-endDate">
              <b-form-input id="create-endDate" v-model="createForm.endDate" type="datetime-local" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-checkbox v-model="createForm.enabled">Enable device immediately</b-form-checkbox>
          </b-col>
        </b-row>
      </form>
    </b-modal>

    <!-- Shortcut Result Modal -->
    <b-modal
      v-model="shortcut.show"
      :title="shortcut.device ? shortcut.cmdLabel + ' \u2014 ' + shortcut.device.name : shortcut.cmdLabel"
      size="lg"
      hide-footer
      @hidden="shortcut.result = null; shortcut.error = null"
    >
      <div v-if="shortcut.running" class="text-center py-4">
        <b-spinner variant="primary" style="width:2.5rem;height:2.5rem;"></b-spinner>
        <p class="mt-3 text-muted mb-0">{{ shortcut.statusMsg }}</p>
      </div>
      <div v-else-if="shortcut.error" class="alert alert-danger mb-0">
        <i class="fa fa-exclamation-triangle mr-1"></i> {{ shortcut.error }}
      </div>
      <div v-else-if="shortcut.result">
        <div v-if="shortcut.result._success" class="alert alert-success mb-0">
          <i class="fa fa-check-circle mr-1"></i> {{ shortcut.result._success }}
        </div>
        <div v-else-if="shortcut.result._raw" class="alert alert-secondary mb-0" style="font-family:monospace;white-space:pre-wrap;font-size:0.85rem;">{{ shortcut.result._raw }}</div>
        <div v-else-if="shortcut.result._sections">
          <div v-for="(sectionData, sectionKey) in shortcut.result._sections" :key="sectionKey" class="mb-3">
            <h6 class="text-uppercase text-muted mb-2" style="font-size:0.7rem;letter-spacing:0.08em;border-bottom:1px solid #dee2e6;padding-bottom:4px;">{{ formatSectionTitle(sectionKey) }}</h6>
            <b-row>
              <b-col v-for="(val, key) in sectionData" :key="key" sm="6" md="4" class="mb-2">
                <div class="shortcut-field p-2 border rounded">
                  <small class="text-muted text-uppercase d-block" style="font-size:0.65rem;letter-spacing:0.04em">{{ formatShortcutKey(key) }}</small>
                  <span class="font-weight-bold">{{ formatShortcutValue(key, val) }}</span>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
        <b-row v-else>
          <b-col v-for="(val, key) in shortcut.result" :key="key" sm="6" class="mb-2">
            <div class="shortcut-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem;letter-spacing:0.04em">{{ formatShortcutKey(key) }}</small>
              <span class="font-weight-bold">{{ formatShortcutValue(key, val) }}</span>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- Calibrate Sensor Modal -->
    <b-modal
      v-model="showCalibrateModal"
      :title="calibrateDevice ? 'Calibrate: ' + calibrateDevice.name : 'Calibrate Sensor'"
      size="md"
      hide-footer
      @hidden="resetCalibrateState"
    >
      <!-- Loading: fetching config or mid-setup -->
      <div v-if="calibrateLoading" class="text-center py-5">
        <b-spinner variant="primary" style="width:2.5rem;height:2.5rem;"></b-spinner>
        <p class="mt-3 text-muted mb-0">{{ calibrateSensors.length ? 'Setting up calibration...' : 'Loading sensors...' }}</p>
      </div>

      <!-- Error -->
      <div v-else-if="calibrateError">
        <b-alert show variant="danger" class="mb-3">
          <i class="fa fa-exclamation-triangle mr-1"></i> {{ calibrateError }}
        </b-alert>
        <div class="text-right">
          <b-button variant="secondary" @click="showCalibrateModal = false">Close</b-button>
        </div>
      </div>

      <!-- Single sensor — show confirm card -->
      <div v-else-if="calibrateSensors.length === 1">
        <p class="text-muted small mb-3">
          <i class="fa fa-info-circle mr-1"></i>
          The <code>cal</code> value for <strong>{{ calibrateSensors[0].label }}</strong> will be set to
          <code>0</code> and a config refresh will be sent to the device before opening the calibration screen.
          If the refresh fails the original value will be restored.
        </p>
        <div class="d-flex justify-content-between align-items-center p-3 border rounded mb-4">
          <div>
            <strong>{{ calibrateSensors[0].label }}</strong>
            <b-badge variant="secondary" class="ml-2">{{ calibrateSensors[0].type }}</b-badge>
          </div>
          <span class="text-muted small">
            Current cal: <code>{{ calibrateSensors[0].cal != null ? calibrateSensors[0].cal : 0 }}</code>
          </span>
        </div>
        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="showCalibrateModal = false">Cancel</b-button>
          <b-button variant="primary" @click="startCalibration(calibrateSensors[0])">
            <i class="fa fa-balance-scale mr-1"></i> Start Calibration
          </b-button>
        </div>
      </div>

      <!-- Multiple sensors — list to choose from -->
      <div v-else-if="calibrateSensors.length > 1">
        <p class="text-muted small mb-3">
          <i class="fa fa-info-circle mr-1"></i>
          Select the sensor to calibrate. Its <code>cal</code> value will be zeroed and a config refresh
          sent to the device before opening the calibration screen.
        </p>
        <b-list-group>
          <b-list-group-item
            v-for="sensor in calibrateSensors"
            :key="sensor.id"
            button
            class="d-flex justify-content-between align-items-center"
            @click="startCalibration(sensor)"
          >
            <div>
              <strong>{{ sensor.label }}</strong>
              <b-badge variant="secondary" class="ml-2">{{ sensor.type }}</b-badge>
            </div>
            <div class="text-muted small text-right">
              <span class="mr-2">{{ sensor.iface }} ch{{ sensor.channel }}</span>
              cal: <code>{{ sensor.cal != null ? sensor.cal : 0 }}</code>
              <i class="fa fa-chevron-right ml-2"></i>
            </div>
          </b-list-group-item>
        </b-list-group>
      </div>
    </b-modal>

    <!-- Linked Devices Modal -->
    <b-modal
      v-model="showLinkModal"
      :title="linkModalDevice ? 'Linked Devices: ' + linkModalDevice.name : 'Linked Devices'"
      size="md"
      hide-footer
      @hidden="resetLinkState"
    >
      <div v-if="linkModalDevice">
        <p class="text-muted small mb-3">
          <i class="fa fa-info-circle mr-1"></i>
          Pairing is directionless — it only has to be set from one side. A river paired with a
          well lets the dashboard compute potentiometric head from the two elevations.
        </p>

        <b-list-group v-if="linkedDevices(linkModalDevice).length" class="mb-3">
          <b-list-group-item
            v-for="linked in linkedDevices(linkModalDevice)"
            :key="linked.id"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <strong>{{ linked.name }}</strong>
              <b-badge variant="secondary" class="ml-2">{{ linked.type }}</b-badge>
              <div class="small text-muted">
                {{ linked.serial }}
                <span v-if="linked.elevation !== null && linked.elevation !== undefined">
                  &mdash; elevation {{ linked.elevation }} ft
                </span>
                <span v-else class="text-warning">
                  &mdash; no elevation set, head cannot be computed
                </span>
              </div>
            </div>
            <b-button size="sm" variant="outline-danger" :disabled="linkBusy" @click="removeLink(linked)">
              <i class="fa fa-unlink"></i>
            </b-button>
          </b-list-group-item>
        </b-list-group>

        <p v-else class="text-muted small mb-3">No linked devices.</p>

        <b-form-group label="Link another device" label-for="link-target">
          <b-input-group>
            <b-form-select
              id="link-target"
              v-model="linkTargetId"
              :options="linkCandidateOptions"
              :disabled="linkBusy"
            ></b-form-select>
            <b-input-group-append>
              <b-button variant="primary" :disabled="!linkTargetId || linkBusy" @click="addLink">
                <b-spinner v-if="linkBusy" small></b-spinner>
                <span v-else><i class="fa fa-link mr-1"></i> Link</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>
    </b-modal>

    <!-- Replace Device Modal -->
    <b-modal
      v-model="showReplaceModal"
      :title="replaceDevice ? 'Replace Device: ' + replaceDevice.name : 'Replace Device'"
      size="md"
      @hidden="resetReplaceForm"
      :ok-disabled="!isReplaceReady"
      ok-title="Replace Device"
      ok-variant="warning"
      @ok="executeReplace"
    >
      <div v-if="replaceDevice">
        <b-alert show variant="info" class="py-2 px-3 mb-3">
          <div><strong>Current Serial:</strong> {{ replaceDevice.serial }}</div>
          <div><strong>Type:</strong> {{ replaceDevice.type }}</div>
        </b-alert>

        <b-form-checkbox v-model="replaceForm.addParticle" class="mb-2">
          <strong>Provision Particle Device</strong>
        </b-form-checkbox>
        <p class="text-muted small ml-4 mb-3">
          Claims the new Particle hardware and updates the device record with the serial from the Particle response.
        </p>

        <b-form-group v-if="replaceForm.addParticle" label="New Particle Device ID or Serial Number *" label-for="replace-particleId">
          <b-form-input
            id="replace-particleId"
            v-model="replaceForm.particleDeviceId"
            placeholder="e.g. e00fce68cd7d03658f2740d4 or P028AD114KD4YSP"
          />
          <small class="form-text text-muted">
            Enter the 24-character Particle Device ID or the hardware serial number. The new serial will be obtained from the Particle response.
          </small>
        </b-form-group>

        <b-form-group v-if="!replaceForm.addParticle" label="New Serial Number *" label-for="replace-serial">
          <b-form-input
            id="replace-serial"
            v-model="replaceForm.newSerial"
            placeholder="Enter new hardware serial number"
            :state="replaceForm.newSerial ? true : null"
          />
        </b-form-group>

        <hr>

        <b-form-checkbox v-model="replaceForm.updateMetrics" class="mb-1">
          <strong>Update Metrics History</strong>
        </b-form-checkbox>
        <p class="text-muted small ml-4 mb-0">
          Re-tags all historical sensor data from <code>{{ replaceDevice.serial }}</code> to the new serial,
          keeping the device history continuous.
        </p>

        <b-alert v-if="replaceError" show variant="danger" class="mt-3 mb-0">
          <i class="fa fa-exclamation-triangle mr-1"></i> {{ replaceError }}
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ALL_DEVICES_QUERY, CREATE_DEVICE_MUTATION, UPDATE_DEVICE_MUTATION, UPDATE_METRIC_SERIALS_MUTATION, SITES_QUERY, DEVICE_CONFIG_QUERY, UPDATE_DEVICE_SENSOR_MUTATION, METRICS_QUERY, LINK_DEVICES_MUTATION, UNLINK_DEVICES_MUTATION } from '@/constants/graphql'
import { getAccessToken } from '@/lib/auth'
import { DEVICE_TYPE_OPTIONS } from '@/constants/data'
import DeviceConfigModal from '@/components/DeviceConfigModal.vue'
import DeviceActionsModal from '@/components/DeviceActionsModal.vue'

const API_BASE = (process.env.VUE_APP_API_ENDPOINT || 'https://apiv2.leaf.ai/graphql').replace('/graphql', '')

export default {
  name: 'DeviceSettings',
  components: { DeviceConfigModal, DeviceActionsModal },
  data () {
    return {
      loading: false,
      error: null,
      selectedDevice: null,
      showConfigModal: false,
      showActionsModal: false,

      shortcut: {
        show: false,
        running: false,
        cmdLabel: null,
        device: null,
        result: null,
        error: null,
        statusMsg: ''
      },
      showCreateModal: false,
      deepLinkHandled: false,
      showLinkModal: false,
      linkModalDevice: null,
      linkTargetId: null,
      linkBusy: false,
      showCalibrateModal: false,
      calibrateDevice: null,
      calibrateSensors: [],
      calibrateLoading: false,
      calibrateError: null,
      showReplaceModal: false,
      replaceDevice: null,
      replaceSaving: false,
      replaceError: null,
      replaceForm: {
        newSerial: '',
        addParticle: false,
        particleDeviceId: '',
        updateMetrics: false
      },
      signalData: {},
      currentPage: 1,
      perPage: 10,
      searchTerm: '',
      showDisabledDevices: false,
      createForm: {
        name: '',
        type: '',
        serial: '',
        leafSerial: '',
        version: '',
        subtype: '',
        siteId: null,
        elevation: null,
        latitude: null,
        longitude: null,
        startDate: '',
        endDate: '',
        enabled: false,
        particleDeviceId: '',
        manualEntry: false
      },
      versionOptions: [
        { value: '', text: 'Select version...' },
        { value: 'v0.6', text: 'v0.6' },
        { value: 'v0.7', text: 'v0.7' }
      ],
      deviceTypeOptions: DEVICE_TYPE_OPTIONS,
      fields: [
        { key: 'name', label: 'Device Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true, thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'serial', label: 'Serial', sortable: true, thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' },
        { key: 'site', label: 'Site', sortable: true, thClass: 'd-none d-sm-table-cell', tdClass: 'd-none d-sm-table-cell' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'signal', label: 'Signal', sortable: false, thClass: 'd-none d-md-table-cell', tdClass: 'd-none d-md-table-cell' },
        { key: 'location', label: 'Location', thClass: 'd-none d-lg-table-cell', tdClass: 'd-none d-lg-table-cell' },
        { key: 'actions', label: '', class: 'text-right' }
      ]
    }
  },
  computed: {
    // Every other device that isn't already linked to the one in the modal.
    linkCandidateOptions () {
      if (!this.linkModalDevice) return [{ value: null, text: 'Select device...' }]
      var linkedIds = this.linkedDevices(this.linkModalDevice).map(function (d) { return d.id })
      var selfId = this.linkModalDevice.id
      var options = (this.devices || [])
        .filter(function (d) { return d.id !== selfId && linkedIds.indexOf(d.id) === -1 })
        .map(function (d) {
          return {
            value: d.id,
            text: d.name + ' (' + d.type + ')' + (d.site ? ' — ' + d.site.name : '')
          }
        })
      return [{ value: null, text: 'Select device...' }].concat(options)
    },
    siteOptions () {
      if (!this.sites) return [{ value: null, text: 'Select site...' }]
      return [
        { value: null, text: 'Select site...' },
        ...this.sites.map(site => ({ value: site.id, text: site.name }))
      ]
    },
    isCreateFormValid () {
      var base = !!(this.createForm.name && this.createForm.type && this.createForm.leafSerial && this.createForm.version)
      if (!base) return false
      if (this.createForm.manualEntry) {
        return !!(this.createForm.serial && this.createForm.serial.trim())
      }
      return !!(this.createForm.particleDeviceId && this.createForm.particleDeviceId.trim())
    },
    isReplaceReady () {
      if (this.replaceSaving) return false
      if (this.replaceForm.addParticle) return !!(this.replaceForm.particleDeviceId || '').trim()
      return !!this.replaceForm.newSerial
    },
    filteredDevices () {
      if (!this.devices) return []
      let filtered = this.devices
      if (!this.showDisabledDevices) {
        filtered = filtered.filter(device => device.enabled)
      }
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase()
        filtered = filtered.filter(device =>
          device.name.toLowerCase().includes(term) ||
          device.serial.toLowerCase().includes(term) ||
          device.type.toLowerCase().includes(term) ||
          (device.site && device.site.name && device.site.name.toLowerCase().includes(term))
        )
      }
      return filtered
    }
  },
  apollo: {
    devices: {
      query: ALL_DEVICES_QUERY,
      // Device type/site/elevation are edited elsewhere and the InMemoryCache is
      // persisted to localStorage, so cache-first served edits back stale across reloads.
      fetchPolicy: 'cache-and-network',
      loadingKey: 'loading',
      result (data) {
        this.fetchAllSignals(data.data.devices)
        this.openDeviceFromQuery(data.data.devices)
      },
      error (error) {
        this.error = error
        // eslint-disable-next-line no-console
        console.error('Error loading devices:', error)
      }
    },
    sites: { query: SITES_QUERY }
  },
  methods: {
    // The API stores a pairing once, on the initiating device's relatedTo, so a device's
    // counterparts are the union of both sides. Deduped by id in case both are populated.
    linkedDevices (device) {
      if (!device) return []
      var seen = {}
      var all = (device.relatedTo || []).concat(device.relatedBy || [])
      all.forEach(function (d) { if (d && d.id) seen[d.id] = d })
      return Object.keys(seen).map(function (id) { return seen[id] })
    },
    openLinks (device) {
      this.linkModalDevice = device
      this.linkTargetId = null
      this.showLinkModal = true
    },
    resetLinkState () {
      this.linkModalDevice = null
      this.linkTargetId = null
      this.linkBusy = false
    },
    async addLink () {
      if (!this.linkTargetId || !this.linkModalDevice) return
      await this.mutateLink(LINK_DEVICES_MUTATION, this.linkTargetId, 'Devices linked')
      this.linkTargetId = null
    },
    async removeLink (linked) {
      if (!this.linkModalDevice) return
      await this.mutateLink(UNLINK_DEVICES_MUTATION, linked.id, 'Devices unlinked')
    },
    async mutateLink (mutation, relatedDeviceId, successMessage) {
      var deviceId = this.linkModalDevice.id
      this.linkBusy = true
      try {
        await this.$apollo.mutate({
          mutation: mutation,
          variables: { deviceId: deviceId, relatedDeviceId: relatedDeviceId }
        })
        await this.$apollo.queries.devices.refetch()
        // The refetch replaces the device objects, so re-point the modal at the new one
        // or it would keep rendering the pre-mutation relations.
        this.linkModalDevice = (this.devices || []).find(function (d) { return d.id === deviceId }) || null
        this.$bvToast.toast(successMessage, { title: 'Success', variant: 'success', solid: true })
      } catch (error) {
        this.$bvToast.toast(error.message || 'An unexpected error occurred', {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.linkBusy = false
      }
    },
    async particleApi (method, path, body) {
      const opts = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + getAccessToken()
        }
      }
      if (body) opts.body = JSON.stringify(body)
      const res = await fetch(API_BASE + path, opts)
      if (!res.ok) {
        let errMsg = 'HTTP ' + res.status
        try {
          const json = await res.json()
          if (json && json.error) errMsg = json.error
        } catch (e) { /* ignore */ }
        throw new Error(errMsg)
      }
      return res.json()
    },

    refreshDevices () {
      this.signalData = {}
      this.$apollo.queries.devices.refetch()
    },

    signalLevel (deviceId) {
      var sig = this.signalData[deviceId]
      if (sig == null) return null
      return sig >= 60 ? 4 : sig >= 40 ? 3 : sig >= 20 ? 2 : 1
    },
    signalBarClass (deviceId, bar) {
      var level = this.signalLevel(deviceId)
      if (level === null || bar > level) return 'signal-bar--empty'
      return level >= 3 ? 'signal-bar--good' : level === 2 ? 'signal-bar--ok' : 'signal-bar--poor'
    },

    async fetchAllSignals (devices) {
      var self = this
      var enabled = (devices || []).filter(function (d) { return d.enabled && d.type !== 'rain' })
      enabled.forEach(function (device) {
        self.fetchSignal(device)
      })
    },

    async fetchSignal (device) {
      try {
        var result = await this.$apollo.query({
          query: METRICS_QUERY,
          variables: {
            where: {
              device: device.serial,
              type: device.type,
              metric: 'cell_sig',
              start: '30d-ago',
              limit: 1
            }
          },
          fetchPolicy: 'network-only'
        })
        var data = result.data.metrics && result.data.metrics.data
        if (!data || !Object.keys(data).length) { this.$set(this.signalData, device.id, null); return }
        var deviceData = data[Object.keys(data)[0]]
        if (!deviceData || !deviceData.values || !deviceData.values.length) { this.$set(this.signalData, device.id, null); return }
        var columns = deviceData.columns
        var sigIndex = columns.indexOf('cell_sig')
        if (sigIndex === -1) { this.$set(this.signalData, device.id, null); return }
        // Get the most recent row
        var timeIndex = columns.indexOf('time') !== -1 ? columns.indexOf('time') : 2
        var rows = deviceData.values.filter(function (r) { return r[sigIndex] != null })
        if (!rows.length) { this.$set(this.signalData, device.id, null); return }
        var latest = rows.reduce(function (a, b) { return b[timeIndex] > a[timeIndex] ? b : a })
        this.$set(this.signalData, device.id, latest[sigIndex])
      } catch (e) {
        this.$set(this.signalData, device.id, null)
      }
    },

    // Deep link from Device Health: /settings/devices?device=<serial> filters the table
    // to that device and opens its configuration. Guarded so the cache-and-network
    // fetch policy's second result does not reopen a modal the user just closed.
    openDeviceFromQuery (devices) {
      var serial = this.$route.query.device
      if (!serial || this.deepLinkHandled) return
      var device = (devices || []).find(function (d) { return d.serial === serial })
      if (!device) return

      this.deepLinkHandled = true
      this.searchTerm = serial
      // The table hides disabled devices by default, but Device Health lists them.
      if (!device.enabled) this.showDisabledDevices = true
      this.editDevice(device)
    },

    editDevice (device) {
      this.selectedDevice = device
      this.showConfigModal = true
    },

    openActions (device) {
      this.selectedDevice = device
      this.showActionsModal = true
    },



    closeConfigModal () {
      this.showConfigModal = false
      this.selectedDevice = null
    },

    onDeviceConfigUpdated () {
      this.closeConfigModal()
      this.$bvToast.toast('Device configuration updated successfully!', {
        title: 'Success', variant: 'success', solid: true, autoHideDelay: 5000
      })
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString()
    },

    async createDevice (bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault()
      if (!this.isCreateFormValid) return

      const useParticle = !this.createForm.manualEntry
      const particleDeviceId = useParticle ? this.createForm.particleDeviceId.trim() : ''
      const deviceName = this.createForm.name
      let serial = this.createForm.serial

      try {
        // If provisioning, do it first to get the Particle device ID as the serial
        if (useParticle && particleDeviceId) {
          const isParticleId = /^[0-9a-f]{24}$/i.test(particleDeviceId)
          const provisionBody = isParticleId
            ? { particleDeviceId: particleDeviceId, name: deviceName }
            : { serialNumber: particleDeviceId, name: deviceName }
          const particleDevice = await this.particleApi('POST', '/api/particle/devices/provision', provisionBody)
          serial = particleDevice.leafSerial
        }

        const deviceInput = {
          name: deviceName,
          type: this.createForm.type,
          serial: serial,
          leafSerial: this.createForm.leafSerial,
          version: this.createForm.version,
          enabled: this.createForm.enabled
        }
        if (this.createForm.subtype) deviceInput.subtype = this.createForm.subtype
        if (this.createForm.elevation !== null && this.createForm.elevation !== '') {
          deviceInput.elevation = parseFloat(this.createForm.elevation)
        }
        if (this.createForm.latitude !== null && this.createForm.latitude !== '') {
          deviceInput.latitude = parseFloat(this.createForm.latitude)
        }
        if (this.createForm.longitude !== null && this.createForm.longitude !== '') {
          deviceInput.longitude = parseFloat(this.createForm.longitude)
        }
        if (this.createForm.startDate) deviceInput.startDate = new Date(this.createForm.startDate).toISOString()
        if (this.createForm.endDate) deviceInput.endDate = new Date(this.createForm.endDate).toISOString()
        if (this.createForm.siteId) deviceInput.site = { connect: [{ id: this.createForm.siteId }] }

        await this.$apollo.mutate({ mutation: CREATE_DEVICE_MUTATION, variables: { input: deviceInput } })

        this.showCreateModal = false
        this.resetCreateForm()
        this.$apollo.queries.devices.refetch()
        this.$bvToast.toast(
          useParticle && particleDeviceId
            ? 'Device "' + deviceName + '" created and provisioned in Particle Cloud (serial: ' + serial + ').'
            : 'Device "' + deviceName + '" created successfully.',
          { title: 'Device Created', variant: 'success', solid: true, autoHideDelay: 5000 }
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating device:', error)
        this.$bvToast.toast(error.message || 'An unexpected error occurred', {
          title: 'Error Creating Device', variant: 'danger', solid: true
        })
      }
    },

    resetCreateForm () {
      this.createForm = {
        name: '', type: '', serial: '', leafSerial: '', version: '', subtype: '',
        siteId: null, elevation: null, latitude: null, longitude: null,
        startDate: '', endDate: '', enabled: false, particleDeviceId: '',
        manualEntry: false
      }
    },

    // ── Command Shortcuts ──────────────────────────────────────────────────────

    async runShortcut (device, cmdId, cmdLabel, waitForEvent) {
      this.shortcut.device = device
      this.shortcut.cmdLabel = cmdLabel
      this.shortcut.result = null
      this.shortcut.error = null
      this.shortcut.running = true
      this.shortcut.statusMsg = 'Looking up device in Particle Cloud\u2026'
      this.shortcut.show = true

      var sentAt = Date.now()

      try {
        // Find the Particle device
        var allDevices = await this.particleApi('GET', '/api/particle/devices')
        var serial = (device.serial || '').toLowerCase()
        var particleDevice = (allDevices || []).find(function (pd) {
          return (pd.serialNumber && pd.serialNumber.toLowerCase() === serial) ||
                 (pd.particleId && pd.particleId.toLowerCase() === serial)
        }) || null

        if (!particleDevice) {
          throw new Error('No Particle device found matching serial ' + device.serial)
        }

        this.shortcut.statusMsg = 'Sending ' + cmdLabel + '\u2026'
        await this.particleApi('POST', '/api/particle/devices/' + particleDevice.id + '/commands', {
          commandName: cmdId,
          argument: '',
          useCorrelation: false
        })

        if (waitForEvent) {
          this.shortcut.statusMsg = 'Waiting for device response\u2026'
          await new Promise(function (resolve) { setTimeout(resolve, 7000) })
          var rawEvents = await this.particleApi('GET', '/api/particle/devices/' + particleDevice.id + '/events?limit=30')
          var evts = Array.isArray(rawEvents) ? rawEvents : (rawEvents.events || [])
          var newEvts = evts.filter(function (e) {
            return new Date(e.publishedAt || e.created).getTime() >= sentAt
          })
          this.shortcut.result = newEvts.length
            ? this.parseShortcutEvent(newEvts[0].data)
            : { _raw: 'Command sent \u2014 no response event received within 7 seconds.' }
        } else {
          this.shortcut.result = { _success: cmdLabel + ' sent successfully.' }
        }
      } catch (e) {
        this.shortcut.error = e.message
      } finally {
        this.shortcut.running = false
      }
    },

    parseShortcutEvent (data) {
      if (!data) return { _raw: 'No data in response event.' }
      try {
        var parsed = JSON.parse(data)
        if (typeof parsed !== 'object' || Array.isArray(parsed)) return { _raw: String(data) }
        // Check if any top-level value is itself an object (nested sections)
        var hasNestedObjects = Object.keys(parsed).some(function (k) {
          return parsed[k] !== null && typeof parsed[k] === 'object' && !Array.isArray(parsed[k])
        })
        if (hasNestedObjects) return { _sections: parsed }
        return parsed
      } catch (e) {
        return { _raw: String(data) }
      }
    },

    formatSectionTitle (key) {
      var titles = {
        device: 'Device Info',
        power: 'Power',
        sensors: 'Sensor Readings'
      }
      return titles[key] || key.replace(/_/g, ' ')
    },

    formatShortcutKey (key) {
      var labels = {
        // Device info
        serial: 'Serial', type: 'Type', company: 'Company',
        hwver: 'Hardware Version', fwver: 'Firmware Version',
        freemem: 'Free Memory', uptime: 'Uptime',
        cell_sig: 'Cell Signal', cell_qual: 'Cell Quality',
        debug: 'Debug Mode', stayAwake: 'Stay Awake',
        time: 'Device Time',
        // Power
        bat_v: 'Battery Voltage', bat_chg: 'Battery Charge',
        bat_state: 'Battery State', bat_src: 'Power Source',
        in_v: 'Input Voltage', in_c: 'Input Current', in_w: 'Input Power',
        // Sensors
        tmp_f: 'Temperature', tmp_c: 'Temperature',
        pres_hpa: 'Pressure', hum_pct: 'Humidity',
        depth_f: 'Depth',
        // Legacy
        soc: 'State of Charge', v: 'Voltage', c: 'Current', t: 'Temp',
        temp: 'Temperature', level: 'Water Level', pressure: 'Pressure',
        firmware: 'Firmware', rssi: 'Signal (RSSI)',
        bat: 'Battery', batt: 'Battery %', pct: 'Percent',
        stage: 'Stage', flow: 'Flow Rate', depth: 'Depth',
        humid: 'Humidity', humidity: 'Humidity', lux: 'Light (lux)',
        lat: 'Latitude', lng: 'Longitude', alt: 'Altitude'
      }
      return labels[key] || key.replace(/_/g, ' ')
    },

    formatShortcutValue (key, val) {
      if (val === null || val === undefined) return '\u2014'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      // Unix timestamp (device.time is seconds since epoch, large integer)
      if (key === 'time' && typeof val === 'number' && val > 1000000000) {
        return new Date(val * 1000).toLocaleString()
      }
      // uptime in seconds → human-readable
      if (key === 'uptime' && typeof val === 'number') {
        var h = Math.floor(val / 3600)
        var m = Math.floor((val % 3600) / 60)
        var s = val % 60
        return (h ? h + 'h ' : '') + (m ? m + 'm ' : '') + s + 's'
      }
      // freemem in bytes → KB
      if (key === 'freemem' && typeof val === 'number') {
        return (val / 1024).toFixed(1) + ' KB'
      }
      var units = {
        bat_v: ' V', in_v: ' V',
        bat_chg: '%',
        in_c: ' A',
        in_w: ' W',
        cell_sig: ' dBm', cell_qual: '%',
        tmp_f: ' °F', tmp_c: ' °C',
        pres_hpa: ' hPa', hum_pct: '%',
        depth_f: ' ft',
        soc: '%', v: ' V', c: ' A', t: ' °C', temp: ' °C',
        level: ' m', depth: ' ft', pressure: ' kPa',
        flow: ' m\u00b3/s', stage: ' m', humidity: ' %', humid: ' %', lux: ' lx'
      }
      var numericVal = typeof val === 'number' ? val : parseFloat(val)
      if (!isNaN(numericVal) && typeof val === 'number') {
        // Round to 3 decimal places max
        var rounded = Math.round(numericVal * 1000) / 1000
        return String(rounded) + (units[key] || '')
      }
      return String(val) + (units[key] || '')
    },

    // ── Calibrate Sensor ───────────────────────────────────────────────────────

    async openCalibrate (device) {
      this.calibrateDevice = device
      this.calibrateSensors = []
      this.calibrateError = null
      this.calibrateLoading = true
      this.showCalibrateModal = true

      try {
        const result = await this.$apollo.query({
          query: DEVICE_CONFIG_QUERY,
          variables: { serial: device.serial },
          fetchPolicy: 'network-only'
        })
        const config = result.data.deviceConfigBySerial
        if (!config || !config.sensors || config.sensors.length === 0) {
          this.calibrateError = 'This device has no sensors configured.'
          return
        }
        this.calibrateSensors = config.sensors
      } catch (err) {
        this.calibrateError = 'Failed to load device sensors: ' + err.message
      } finally {
        this.calibrateLoading = false
      }
    },

    startCalibration (sensor) {
      this.showCalibrateModal = false
      this.resetCalibrateState()
      this.$router.push({ name: 'Sensor Calibration', params: { sensorId: sensor.id } })
    },

    resetCalibrateState () {
      this.calibrateDevice = null
      this.calibrateSensors = []
      this.calibrateError = null
      this.calibrateLoading = false
    },

    // ── Replace Device ─────────────────────────────────────────────────────────

    openReplace (device) {
      this.replaceDevice = device
      this.replaceError = null
      this.replaceForm = { newSerial: '', addParticle: false, particleDeviceId: '', updateMetrics: false }
      this.showReplaceModal = true
    },

    resetReplaceForm () {
      this.replaceDevice = null
      this.replaceError = null
      this.replaceForm = { newSerial: '', addParticle: false, particleDeviceId: '', updateMetrics: false }
      this.replaceSaving = false
    },

    async executeReplace (bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault()
      if (!this.isReplaceReady) return

      this.replaceSaving = true
      this.replaceError = null

      const oldSerial = this.replaceDevice.serial
      const newParticleDeviceId = this.replaceForm.particleDeviceId.trim()
      let newSerial = this.replaceForm.addParticle ? '' : this.replaceForm.newSerial.trim()
      const deviceId = this.replaceDevice.id
      const deviceName = this.replaceDevice.name

      try {
        // 1. Provision in Particle Cloud first (before touching the device record)
        if (this.replaceForm.addParticle) {
          const notes = 'Replaced ' + new Date().toISOString().slice(0, 10) + ' — old serial: ' + oldSerial
          const isParticleId = /^[0-9a-f]{24}$/i.test(newParticleDeviceId)
          var particleResult
          if (isParticleId) {
            // Find existing ParticleDevice to PATCH, otherwise provision fresh
            const allParticleDevices = await this.particleApi('GET', '/api/particle/devices')
            const existing = allParticleDevices.find(function (d) { return d.serialNumber === oldSerial })
            if (existing) {
              particleResult = await this.particleApi('PATCH', '/api/particle/devices/' + existing.id, {
                particleDeviceId: newParticleDeviceId,
                name: deviceName,
                notes: notes
              })
            } else {
              particleResult = await this.particleApi('POST', '/api/particle/devices/provision', {
                particleDeviceId: newParticleDeviceId,
                name: deviceName,
                notes: notes
              })
            }
          } else {
            // Serial provided — provision directly by serial number
            particleResult = await this.particleApi('POST', '/api/particle/devices/provision', {
              serialNumber: newParticleDeviceId,
              name: deviceName,
              notes: notes
            })
          }
          newSerial = particleResult.leafSerial
        }

        // 2. Update the Device serial in GraphQL
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_MUTATION,
          variables: { id: deviceId, input: { serial: newSerial } }
        })

        // 3. Optionally migrate historical metrics from old serial to new serial
        if (this.replaceForm.updateMetrics) {
          try {
            const result = await this.$apollo.mutate({
              mutation: UPDATE_METRIC_SERIALS_MUTATION,
              variables: {
                input: { deviceId, oldSerial, newSerial }
              }
            })
            const count = result.data.updateMetricSerials.count
            const deviceType = result.data.updateMetricSerials.deviceType
            this.$bvToast.toast(
              count + ' ' + deviceType + ' metric records re-tagged from "' + oldSerial + '" to "' + newSerial + '".',
              { title: 'Metrics Updated', variant: 'info', solid: true, autoHideDelay: 6000 }
            )
          } catch (metricsErr) {
            this.$bvToast.toast('Serial updated, but metrics migration failed: ' + metricsErr.message, {
              title: 'Metrics Migration Failed', variant: 'warning', solid: true
            })
          }
        }

        this.showReplaceModal = false
        this.$apollo.queries.devices.refetch()
        this.$bvToast.toast(
          'Device serial updated from "' + oldSerial + '" to "' + newSerial + '".',
          { title: 'Device Replaced', variant: 'success', solid: true, autoHideDelay: 5000 }
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Replace device error:', err)
        this.replaceError = err.message || 'Failed to update device serial.'
      } finally {
        this.replaceSaving = false
      }
    }
  }
}
</script>

<style scoped>
.card-header-actions {
  float: right;
}

/* The row action menu has nine entries. When a row sits low on the page Popper flips the
   menu upwards, and at full height it is taller than the space above the toggle — the
   top entries were being cut off. Capping the height lets it always fit and scroll
   internally instead. Deep selector because the menu is rendered by b-dropdown, not by
   this component's template. */
>>> .device-actions-menu {
  max-height: 60vh;
  overflow-y: auto;
}

.signal-bars {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 16px;
}
.signal-bar {
  width: 5px;
  border-radius: 1px;
}
.signal-bar:nth-child(1) { height: 4px; }
.signal-bar:nth-child(2) { height: 7px; }
.signal-bar:nth-child(3) { height: 11px; }
.signal-bar:nth-child(4) { height: 15px; }
.signal-bar--good  { background-color: #28a745; }
.signal-bar--ok    { background-color: #ffc107; }
.signal-bar--poor  { background-color: #dc3545; }
.signal-bar--empty { background-color: #dee2e6; }


.shortcut-field {
  background: #f8f9fa;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(359deg); }
}
</style>
